import React, { FunctionComponent, useEffect, useState, useCallback, useMemo } from 'react';
import { useStore } from 'effector-react';
import { useParams } from 'react-router-dom';
import { Button, Spin } from 'antd';
import { ArrowRightOutlined, ArrowLeftOutlined, CaretLeftOutlined } from '@ant-design/icons';
import { get } from 'lodash';
import useTranslate from '@hooks/useTranslate';
import componentContent from '../UserInfo/UserInfo.content.json';
import user, { getPosts } from '@stores/user';
import posts from '@stores/posts';
import PostCard from '@components/PostCard';
import IPost from '@interfaces/IPost';
import { goTo } from '@helpers/navigation';
import css from './UserDetailPosts.module.less';

interface UserDetailPostsProps { }

const UserDetailPosts: FunctionComponent<UserDetailPostsProps> = () => {
  const { postId } = useParams<{ postId: IPost['id'] }>();
  const { item, preloaders: userPreloaders, posts: postsList, pagination } = useStore(user);
  const [slide, setSlide] = useState(-1);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const content = useTranslate(componentContent);

  const maxPage = useMemo(() => {
    return Math.ceil(Number(item?.postsCount) / Number(pagination.pageSize));
  }, [item?.postsCount, pagination.pageSize]);

  useEffect(() => {
    if (item && pagination.current === 1 && !!userPreloaders.getPosts && !postsList.length) {
      getPosts({
        userId: item?.id || '',
        pagination: {
          pageSize: item?.postsCount,
          current: 0
        }
      });
    }
  }, [item]);

  const fetchPosts = async (currentPage?: number) => {
    return await getPosts({
      userId: item?.id || '',
      pagination: {
        current: currentPage || pagination.current,
        pageSize: pagination.pageSize
      }
    });
  };

  const { preloaders } = useStore(posts);

  const goToPost = useCallback(async (currentSlide: number, nextSlide: number) => {
    let result;
    if (nextSlide >= postsList.length) {
      setSlide(() => 0);
      result = await fetchPosts(Number(pagination.current) + 1);
    }
    if (nextSlide < 0) {
      setSlide(() => postsList.length - 1);
      result = await fetchPosts(Number(pagination.current) - 1);
    }
    const list = get(result, ['data', 'content']);
    let currentPost = postsList.find((post: IPost, index: number) => index === nextSlide);
    if (list) {
      let compareIndex = 0;
      if (nextSlide < 0) {
        compareIndex = list.length - 1;
      }
      currentPost = list?.find((post: IPost, index: number) => index === compareIndex);
    }
    if (currentPost?.id) {
      goTo('userDetailPosts', { userId: item?.id, postId: currentPost.id });
    }
  }, [slide, setSlide, postsList]);

  useEffect(() => {
    setSlide(() => postsList.findIndex((post) => String(post.id) === postId));
  }, [postId]);

  useEffect(() => {
    if (pagination.current === 1 && postsList.length) {
      setSlide(() => postsList.findIndex((post) => String(post.id) === postId));
    }
  }, [postsList]);

  useEffect(() => {
    if (slide !== -1) {
      setIsLoading(() => false);
    }
  }, [slide]);

  const goBackToUser = useCallback(() => {
    goTo('userDetail', { userId: item?.id });
  }, [item]);

  return (
    <Spin spinning={!!userPreloaders.getItem || !!userPreloaders.getPosts || isLoading}>
      <div className={css.root}>
        <div className={css.post}>
          <Button
            className={css.goBack}
            type='primary'
            icon={<CaretLeftOutlined />}
            onClick={goBackToUser}
          >
            {content.goBackToUser}
          </Button>
          <div className={css.carouselContainer}>
            <div className={css.carousel}>
              {postsList.map((post, index) => (
                <div
                  key={post.id}
                  className={index !== slide ? css.slideHidden : ''}
                >
                  <PostCard
                    post={post}
                    isBlocking={preloaders.block === `block_${post.id}`}
                    isUnblocking={preloaders.unblock === `unblock_${post.id}`}
                    isHidingPost={preloaders.hidePost === `hidePost_${post.id}`}
                    isUnhidingPost={preloaders.unhidePost === `unhidePost_${post.id}`}
                  />
                </div>
              ))}
            </div>
            <div className={css.postArrows}>
              <div>
                {slide > 0 || Number(pagination.current) > 0 ? (
                  <Button onClick={() => goToPost(slide, slide - 1)}>
                    <ArrowLeftOutlined />
                  </Button>
                ) : null}
              </div>
              <div>
                {slide <= postsList.length - 1 && Number(pagination.current) <= maxPage ? (
                  <Button onClick={() => goToPost(slide, slide + 1)}>
                    <ArrowRightOutlined />
                  </Button>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div >
    </Spin>
  );
};

export default UserDetailPosts;
